import { AnyAction, bindActionCreators, Dispatch } from 'redux';
import {
  BorderGridContainer,
  ErrorSummary,
  GridBox,
  H1,
  Hidden,
  Image,
  P,
  scrollToFirstError,
  SpacerWrapper,
} from '@ahmdigital/ui';
import { compact, isEmpty, pick } from 'lodash/fp';
import { connect } from 'react-redux';
import { Formik } from 'formik';
import { INCOME_TYPE } from '@ahmdigital/constants';
import { useFeatureValue } from '@growthbook/growthbook-react';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import {
  GROWTHBOOK_CHOOSE_YOUR_EXTRAS_COVER_EXPERIMENT,
  GROWTHBOOK_CHOOSE_YOUR_EXTRAS_COVER_VARIANT,
} from '../../ahm-constants/ui';
import { Offer, Placeholder } from '../../components/offers/types';
import buildSubmitHandler from './utils/build-submit-handler';
import CustomerActions from '../../actions/customer';
import filterOffersByProductType from '../../utils/filter-offers-by-product-type';
import getDestinationUrl from './utils/get-destination-url';
import getRefinePriceQueryValues from '../../utils/get-refine-price-query-values';
import OfferBanner from '../../components/offers/banner';
import paperPlaneImage from '../../images/paper-plane.svg';
import RefinePriceForm from './form';
import refinePriceSchema, { RefinePriceSchemaType } from './form/schema';
import selectCustomer from '../../selectors/select-customer';
import selectCustomerFormInitialValues from '../../components/pricing-drawer/selectors/select-customer-form-initial-values';
import selectHasPartnerReferrer from '../../selectors/select-has-partner-referrer';
import selectTargetSegment from '../../selectors/select-target-segment';
import type { RefinePriceActions } from './types';

const SCROLL_OFFSET = 100;

const Wrapper = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
  position: relative;
  z-index: 1;

  @media (min-width: ${({ theme }) => theme.media.xs}) {
    border: 1px dotted ${({ theme }) => theme.colors.black};
    margin: 0 auto;
    max-width: 450px;
    padding: 2rem;
  }
`;

const StyledImage = styled(Image)`
  position: absolute;
  top: 375px;
  left: 50%;
  width: 700px;
  transform: translateX(-50%);

  @media (min-width: ${({ theme }) => theme.media.lg}) {
    width: 1100px;
  }
`;

const StyledErrorWrapper = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
  position: relative;
  z-index: 1;

  @media (min-width: ${({ theme }) => theme.media.xs}) {
    margin: 0 auto;
    max-width: 450px;
  }
`;

export type RefinePricePageProps = {
  actions: RefinePriceActions;
  content: {
    activeOffers?: Offer[];
    placeholders?: Placeholder[];
  };
  customer: {
    scale: string;
    rebate: boolean;
  };
  initialValues: RefinePriceSchemaType;
  isPartnerReferral: boolean;
  targetSegment?: string;
};

const schemaForErrorSummary = {
  age: {
    linkPhrases: ['age'],
  },
  partnerAge: {
    linkPhrases: ['partner’s age'],
  },
  productType: {
    linkPhrases: ['health cover type'],
  },
};
const errorOrder = ['productType', 'age', 'partnerAge'];

const RefinePricePage = ({
  actions,
  content,
  customer,
  initialValues,
  isPartnerReferral,
  targetSegment,
}: RefinePricePageProps) => {
  const router = useRouter();

  const [formikErrors, setFormikErrors] = useState({});
  const [formikTouched, setFormikTouched] = useState({});
  const [productTypeForOffers, setProductTypeForOffers] = useState(initialValues?.productType);
  const [scaleQuery, setScaleQuery] = useState({ incomeType: '', scale: '' });

  const activeOffers = content?.activeOffers;
  // @ts-expect-error - Automatic, Please fix when editing this file
  const filteredOffers = filterOffersByProductType(productTypeForOffers, activeOffers) as Offer;
  const offerProps = {
    overrideOffers: compact([filteredOffers]),
    placeholders: content?.placeholders,
    targetSegment,
  };

  const chooseYourExtrasValue = useFeatureValue(
    GROWTHBOOK_CHOOSE_YOUR_EXTRAS_COVER_EXPERIMENT,
    GROWTHBOOK_CHOOSE_YOUR_EXTRAS_COVER_VARIANT.ORIGINAL,
  );
  const isChooseYourExtras = chooseYourExtrasValue === GROWTHBOOK_CHOOSE_YOUR_EXTRAS_COVER_VARIANT.NEW;

  useEffect(() => {
    if (!router.isReady) return;
    const { scale: scaleParam } = getRefinePriceQueryValues({ params: router?.query });
    if (scaleParam) {
      setScaleQuery({
        incomeType: scaleParam === INCOME_TYPE.SINGLE ? INCOME_TYPE.SINGLE : INCOME_TYPE.COUPLE,
        scale: scaleParam,
      });
    }
  }, [router, router.isReady]);

  return (
    <BorderGridContainer hideBorderTop>
      {!isPartnerReferral && filteredOffers && <OfferBanner {...offerProps} />}
      <GridBox padding="1rem">
        <SpacerWrapper variant={['marginBottomSpacer4', 'marginTopSpacer9']}>
          <Hidden xs sm>
            <StyledImage src={paperPlaneImage.src} />
          </Hidden>
          {!isEmpty(formikErrors) && (
            <StyledErrorWrapper>
              <ErrorSummary
                schema={schemaForErrorSummary}
                errors={pick(errorOrder, formikErrors)}
                touched={formikTouched}
              />
            </StyledErrorWrapper>
          )}
          <Wrapper>
            <H1 variant="marginBottomSpacer10" looksLike="h3">
              Let’s find your cover
            </H1>
            <P variant="marginBottomSpacer8">Answer a few quick questions to tailor your health insurance results.</P>
            <Formik<RefinePriceSchemaType>
              enableReinitialize
              validationSchema={refinePriceSchema}
              initialValues={
                isEmpty(scaleQuery.scale)
                  ? initialValues
                  : { ...initialValues, incomeType: scaleQuery.incomeType, scale: scaleQuery.scale }
              }
              onSubmit={(values: any) => {
                buildSubmitHandler({ actions, customer, formValues: values, initialValues });
                router.push(getDestinationUrl(values.productType, isChooseYourExtras));
              }}
            >
              {({ isSubmitting, errors, submitCount }) => {
                if (isSubmitting && !isEmpty(errors)) {
                  scrollToFirstError({ offset: SCROLL_OFFSET });
                }
                return (
                  <RefinePriceForm
                    captureFormState={{ setFormikErrors, setFormikTouched }}
                    handleChangeProductType={setProductTypeForOffers}
                    hasSubmitAttempt={Boolean(submitCount)}
                  />
                );
              }}
            </Formik>
          </Wrapper>
        </SpacerWrapper>
      </GridBox>
    </BorderGridContainer>
  );
};

const mapStateToProps = (state: object) => ({
  customer: selectCustomer(state),
  initialValues: selectCustomerFormInitialValues(state),
  isPartnerReferral: selectHasPartnerReferrer(state),
  targetSegment: selectTargetSegment(state),
});

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) => ({
  actions: {
    customer: bindActionCreators(CustomerActions, dispatch),
  },
});

// @ts-expect-error - Automatic, Please fix when editing this file
export default connect(mapStateToProps, mapDispatchToProps)(RefinePricePage);

export { RefinePricePage as RefinePricePageForTest };
